import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import { SavedFilter } from '@/shared/components/entities/AdvancedFilters'

interface QuickFiltersButtonProps {
  savedFilters: SavedFilter[]
  onLoadFilter: (filterId: string) => void
  onClearFilters: () => void
  onOpenAdvancedFilters: () => void
  activeFiltersCount?: number
  // System filter: show only selected items
  selectedCount?: number
  showOnlySelected?: boolean
  onToggleShowSelected?: () => void
}

export function QuickFiltersButton({
  savedFilters,
  onLoadFilter,
  onClearFilters,
  onOpenAdvancedFilters,
  activeFiltersCount = 0,
  selectedCount = 0,
  showOnlySelected = false,
  onToggleShowSelected
}: QuickFiltersButtonProps) {
  const { t } = useTranslation()
  const totalActiveCount = activeFiltersCount + (showOnlySelected ? 1 : 0)
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({})
  const [useFixed, setUseFixed] = useState(false)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Calculate dropdown position to avoid viewport overflow (mainly on mobile)
  useEffect(() => {
    if (!isOpen || !buttonRef.current) return

    const isMobile = window.innerWidth < 768
    if (!isMobile) {
      setUseFixed(false)
      setDropdownStyle({})
      return
    }

    const rect = buttonRef.current.getBoundingClientRect()
    const dropdownWidth = Math.min(320, window.innerWidth - 16)
    const top = rect.bottom + 8
    const maxHeight = window.innerHeight - top - 8
    // Align right edge with button's right edge, but clamp so dropdown never
    // overflows left or right side of the viewport.
    const rightFromViewport = window.innerWidth - rect.right
    const right = Math.min(
      Math.max(rightFromViewport, 8),
      window.innerWidth - dropdownWidth - 8
    )

    setUseFixed(true)
    setDropdownStyle({
      position: 'fixed',
      top,
      right,
      width: dropdownWidth,
      maxHeight,
      overflowY: 'auto',
    })
  }, [isOpen])

  const handleLoadFilter = (filterId: string) => {
    onLoadFilter(filterId)
    setIsOpen(false)
  }

  const handleOpenAdvanced = () => {
    setIsOpen(false)
    onOpenAdvancedFilters()
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Botão de Filtro */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="p-1 text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors shrink-0 relative"
        title={t('list.quickFilters.buttonTitle')}
      >
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
        </svg>
        {totalActiveCount > 0 && (
          <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-white text-[10px] font-bold">
            {totalActiveCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className={`${useFixed ? '' : 'absolute right-0 mt-2 w-80'} bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl z-50`}
          style={dropdownStyle}
        >
          {/* Header */}
          <div className="p-3 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                {t('list.quickFilters.title')}
              </h3>
              {totalActiveCount > 0 && (
                <button
                  onClick={() => {
                    onClearFilters()
                    setIsOpen(false)
                  }}
                  className="text-xs font-medium text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors flex items-center gap-1"
                  title={t('list.quickFilters.clear')}
                >
                  <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  {t('list.quickFilters.clear')}
                </button>
              )}
            </div>
          </div>

          {/* System filter: show only selected */}
          <div className={savedFilters.length > 0 ? 'border-b border-slate-200 dark:border-slate-700' : ''}>
            <div className="py-1">
              <button
                onClick={() => {
                  if (selectedCount > 0) {
                    onToggleShowSelected?.()
                    setIsOpen(false)
                  }
                }}
                disabled={selectedCount === 0}
                className={`w-full px-3 py-2 text-left flex items-center gap-3 transition-colors ${
                  showOnlySelected
                    ? 'bg-emerald-50 dark:bg-emerald-900/20'
                    : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'
                } disabled:opacity-40 disabled:cursor-not-allowed`}
              >
                <svg
                  className={`h-4 w-4 shrink-0 ${showOnlySelected ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400'}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${showOnlySelected ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-900 dark:text-white'}`}>
                    {t('list.quickFilters.showSelected')}
                  </p>
                  {selectedCount > 0 && (
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {t('list.quickFilters.showSelectedHint', { count: String(selectedCount) })}
                    </p>
                  )}
                </div>
                {showOnlySelected && (
                  <span className="px-1.5 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-xs rounded shrink-0">
                    {t('list.quickFilters.activeBadge')}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Filtros Salvos */}
          {savedFilters.length > 0 && (
            <div className="max-h-72 overflow-y-auto py-1">
              {savedFilters.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => handleLoadFilter(filter.id)}
                  className="w-full px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <svg className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                        </svg>
                        <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                          {filter.name}
                        </p>
                        {filter.is_default && (
                          <span className="px-1.5 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-xs rounded shrink-0">
                            {t('list.quickFilters.defaultBadge')}
                          </span>
                        )}
                      </div>
                      
                      {/* Preview dos filtros ativos */}
                      {Object.keys(filter.filters).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5 ml-6">
                          {Object.entries(filter.filters)
                            .filter(([_, value]) => {
                              if (value === null || value === undefined || value === '') return false
                              if (typeof value === 'object' && !Array.isArray(value)) {
                                return Object.values(value).some(v => v !== null && v !== undefined && v !== '')
                              }
                              return true
                            })
                            .slice(0, 3)
                            .map(([key, _]) => (
                              <span
                                key={key}
                                className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-xs rounded"
                              >
                                {key}
                              </span>
                            ))}
                          {Object.keys(filter.filters).length > 3 && (
                            <span className="px-1.5 py-0.5 text-slate-500 text-xs">
                              +{Object.keys(filter.filters).length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>

          )}

          {/* Footer - Mais Filtros */}
          <div className="p-2 border-t border-slate-200 dark:border-slate-700">
              <button
              onClick={handleOpenAdvanced}
              className="w-full px-3 py-2 text-sm font-medium text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
              {t('list.quickFilters.moreFilters')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
